/**
 * Shënimi nën fushën «Data e aktivizimit» — i njëjti te regjistrimi dhe te
 * profili, që HR-ja ta lexojë verdiktin ndërsa e shkruan datën.
 *
 * Nuk vendos asgjë: llogarinë e mbyll serveri (`services/activation.js`).
 * Këtu vetëm thuhet çfarë do të ndodhë.
 */
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/apiServices';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { formatDMY, isPendingActivation, toYMD } from '../../utils/activationStatus';

/**
 * Shënimi te «Qasja në sistem» (regjistrimi): çfarë ndodh me ftesën dhe me
 * hyrjen kur ka datë aktivizimi. Serveri e zgjat linkun e ftesës deri 72 orë
 * pas datës dhe e shkruan datën në email — këtu vetëm i thuhet HR-së.
 */
const DAY_SQ = { Sunday: 'e diel', Monday: 'e hënë', Tuesday: 'e martë', Wednesday: 'e mërkurë', Thursday: 'e enjte', Friday: 'e premte', Saturday: 'e shtunë' };
const dayOf = (ymd) => { const [y, m, d] = ymd.split('-').map(Number); return DAY_SQ[['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date(y, m - 1, d).getDay()]]; };

/**
 * Plani i ftesës nga serveri (`/api/users/invite-plan`): dita e fundit e punës
 * para fillimit, në fillim të orarit — i njëjti llogaritje që ndjek dërgimi.
 */
function useInvitePlan(ymd, pending, workerId) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (!ymd || !pending) { setPlan(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      try {
        const qs = `activationDate=${encodeURIComponent(ymd)}${workerId ? `&workerId=${encodeURIComponent(workerId)}` : ''}`;
        authFetch(`/api/users/invite-plan?${qs}`)
          .then((r) => r.json())
          .then((j) => { if (alive) setPlan(j?.data || null); })
          .catch(() => { if (alive) setPlan(null); });
      } catch { /* pa rrjet (teste) — mbetet pa plan */ }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [ymd, pending, workerId]);
  return plan;
}

/**
 * @param inviteAt  { date, time, manual } — koha e zgjedhur për ftesën; kur
 *                  `manual` është false, parambushet nga plani i serverit.
 */
export function AccountActivationNote({ value, sendInvite = true, onChange, workerId = null, inviteAt = null, onInviteAtChange = null }) {
  const ymd = toYMD(value);
  const pending = ymd ? isPendingActivation(ymd) : false;
  const when = ymd ? formatDMY(ymd) : '';
  const plan = useInvitePlan(ymd, pending, workerId);
  // Parambushja: sapo vjen plani, data/ora e ftesës marrin vlerën e tij — derisa
  // HR-ja t'i prekë vetë (`manual`). Pastaj plani nuk i mbishkruan më.
  useEffect(() => {
    if (!onInviteAtChange) return;
    const reset = () => { if (inviteAt && !inviteAt.manual && (inviteAt.date || inviteAt.time)) onInviteAtChange({ date: '', time: '', manual: false }); };
    if (!pending) { reset(); return; }
    if (!plan) return;                       // ende pa plan (ose pa rrjet): mos prek asgjë
    if (plan.immediate || !plan.workingDay) { reset(); return; }
    if (!inviteAt?.manual && (inviteAt?.date !== plan.workingDay || inviteAt?.time !== plan.startTime)) {
      onInviteAtChange({ date: plan.workingDay, time: plan.startTime, manual: false });
    }
  }, [plan, pending, inviteAt, onInviteAtChange]);
  const chosen = inviteAt && inviteAt.date && inviteAt.time ? inviteAt : null;
  // Ftesa: kur aktivizimi është në të ardhmen, del një ditë pune para fillimit,
  // në fillim të orarit (serveri e planifikon dhe e dërgon) — ose në kohën që
  // zgjedh HR-ja këtu. Ndryshe, me «Ruaj».
  const scheduled = pending && ((chosen) || (plan && !plan.immediate && plan.workingDay));
  // Një datë e vetme: ftesa del ditën e aktivizimit; zgjidhet vetëm ora.
  const sendDay = ymd;
  const sendTime = chosen ? chosen.time : (plan?.startTime || '');
  const inviteWhen = !sendInvite
    ? 'nuk dërgohet — fjalëkalim i përkohshëm'
    : scheduled
      ? `${formatDMY(sendDay)} (${dayOf(sendDay)}) në orën ${sendTime}${chosen?.manual
        ? ' — ora e zgjedhur nga ju'
        : ` — ditën e aktivizimit, në fillim të orarit${plan?.schedule?.name ? ` «${plan.schedule.name}»` : ''}`}`
      : pending && !plan
        ? 'ditën e aktivizimit, në fillim të orarit (duke llogaritur…)'
        : 'menjëherë pas «Ruaj» (email me link)';
  const loginWhen = !ymd
    ? 'menjëherë, sapo krijohet llogaria'
    : pending
      ? `${when} (${dayOf(ymd)}) — dita e parë e punës${plan?.startTime ? `, orari nga ${plan.startTime}` : ''}`
      : `${when} ka mbërritur — menjëherë`;
  return (
    <Box sx={{ mt: 1.25 }}>
      <Grid container spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
        <Grid item xs={12} sm={5} md={4}>
          {/* E njëjta fushë si te «Punësimi»: një vlerë, dy vende ku shihet. */}
          <TextField
            size="small" fullWidth type="date" label="Data e aktivizimit"
            InputLabelProps={{ shrink: true }}
            value={ymd}
            onChange={(e) => onChange && onChange(e.target.value)}
            inputProps={{ 'data-testid': 'account-activation-date' }}
          />
        </Grid>
        {/* Ora e ftesës (data është ajo e aktivizimit): e parambushur nga orari, e ndryshueshme. */}
        {sendInvite && pending && onInviteAtChange && (
          <Grid item xs={5} sm={3} md={2}>
            <TextField
              size="small" fullWidth type="time" label="Ftesa në orën"
              InputLabelProps={{ shrink: true }}
              value={inviteAt?.time || ''}
              inputProps={{ step: 300, 'data-testid': 'invite-send-time' }}
              onChange={(e) => onInviteAtChange({ date: ymd, time: e.target.value, manual: true })}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={sendInvite && pending && onInviteAtChange ? 12 : 7} md={sendInvite && pending && onInviteAtChange ? 7 : 8}>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
            <b>Ftesa dërgohet:</b> {inviteWhen}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
            <b>Hyrja hapet:</b> {loginWhen}
          </Typography>
        </Grid>
      </Grid>
      {ymd && (
        <Alert severity={pending ? 'warning' : 'success'} icon={pending ? <HourglassTopRoundedIcon fontSize="inherit" /> : <CheckCircleRoundedIcon fontSize="inherit" />} sx={{ mt: 1, py: 0.5, borderRadius: 1.5 }}>
          {pending ? (
            <>
              <b>Data e aktivizimit: {when}.</b>{' '}
              {sendInvite
                ? `Ftesa dërgohet ${scheduled ? `më ${formatDMY(sendDay)} në ${sendTime}` : 'ditën e aktivizimit, në orar pune'} dhe linku vlen deri 72 orë pas ${when}; hyrja në sistem hapet më ${when}, dita e parë e punës.`
                : `Pa ftesë: llogaria krijohet me fjalëkalim të përkohshëm, por hyrja në sistem hapet vetëm më ${when}, dita e parë e punës.`}
            </>
          ) : (
            <>
              <b>Data e aktivizimit: {when}</b> — data ka mbërritur, llogaria ka qasje sapo krijohet.
            </>
          )}
        </Alert>
      )}
    </Box>
  );
}

export default function ActivationDateHint({ value, hasAccount = true }) {
  const ymd = toYMD(value);
  if (!ymd) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.45 }}>
        Pa datë: llogaria ka qasje në sistem sapo krijohet. Vendos një datë të ardhme që punëtori të
        ekzistojë në HR, kontratë dhe planifikim që tani, por të hyjë vetëm nga ajo ditë.
      </Typography>
    );
  }
  const pending = isPendingActivation(ymd);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Chip
        size="small"
        color={pending ? 'warning' : 'success'}
        icon={pending ? <HourglassTopRoundedIcon sx={{ fontSize: 15 }} /> : <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />}
        label={pending ? `Në pritje të aktivizimit · nga ${formatDMY(ymd)}` : 'Aktiv · data ka mbërritur'}
        sx={{ height: 24, fontWeight: 700 }}
      />
      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.45 }}>
        {pending
          ? (hasAccount
            ? 'Deri atë ditë hyrja refuzohet (edhe me Google/Microsoft, edhe me QR); të dhënat në HR mbeten të plota.'
            : 'Punëtori nuk ka llogari; kur t\'i krijohet, hyrja hapet po atë ditë.')
          : 'Data është sot ose ka kaluar: llogaria hyn normalisht.'}
      </Typography>
    </Box>
  );
}
